import { memo, useMemo } from 'react'
import { Table, TableBody } from '@mui/material'
import { Order } from '../../proto/generated/order'
import { groupIcebergBasketOrders, isIcebergGroup } from '../../util/icebergOrders'
import { OrdersNavButtonsDetailed } from '../postionsWidget'
import OrdersTableHeader from './OrdersTableHeader'
import OrderRow from './OrderRow'
import OrderBatchGroupRow from './OrderBatchGroupRow'

export type OpenOrdersTableProps = {
    orders: Order[]
    tabType: OrdersNavButtonsDetailed
    hideOrderStatus?: boolean
    stickyHeader?: boolean
    tableClassName?: string
}

const OpenOrdersTable: React.FC<OpenOrdersTableProps> = ({
    orders,
    tabType,
    hideOrderStatus = false,
    stickyHeader = false,
    tableClassName = 'font-feature-num',
}) => {
    const groupedOrders = useMemo(() => {
        return groupIcebergBasketOrders(orders).sort((a, b) => {
            const aOrder = isIcebergGroup(a) ? a.allOrders[0] : a
            const bOrder = isIcebergGroup(b) ? b.allOrders[0] : b
            return (bOrder?.created ?? 0) - (aOrder?.created ?? 0)
        })
    }, [orders])

    return (
        <Table stickyHeader={stickyHeader} className={tableClassName} sx={{ tableLayout: 'fixed', width: '100%' }}>
            <OrdersTableHeader tabType={tabType} />
            <TableBody sx={stickyHeader ? { overflow: 'auto' } : undefined}>
                {groupedOrders.map((orderOrGroup) => {
                    const key = isIcebergGroup(orderOrGroup)
                        ? `iceberg-${orderOrGroup.batchId}-${orderOrGroup.basketId}`
                        : orderOrGroup.clientOrderId || orderOrGroup.orderId

                    if (isIcebergGroup(orderOrGroup) && orderOrGroup.isBasketGroup) {
                        return (
                            <OrderBatchGroupRow
                                key={key}
                                batchGroup={orderOrGroup}
                                tabType={tabType}
                                hideMetaData={hideOrderStatus}
                            />
                        )
                    }

                    return (
                        <OrderRow
                            key={key}
                            order={orderOrGroup}
                            tabType={tabType}
                            hideOrderStatus={hideOrderStatus}
                        />
                    )
                })}
            </TableBody>
        </Table>
    )
}

export default memo(
    OpenOrdersTable,
    (prev, next) =>
        prev.orders === next.orders &&
        prev.tabType === next.tabType &&
        prev.hideOrderStatus === next.hideOrderStatus &&
        prev.stickyHeader === next.stickyHeader
)
